import React, { useState, useEffect, useContext } from 'react'
import ApiService from '../../../../services/ApiService'
import {
  Container,
  HeaderDiv,
  HeaderText,
  CloseDiv,
  CloseImg,
  ProgressBarDiv,
  ProgressBarCircle,
  ProgressBarLine,
  InputRowDiv,
  InputDiv,
  InputLabel,
  Input,
  SelectInput,
  SelectOption,
  ButtonsDiv,
  PreviousButtonDiv,
  PreviousButtonImage,
  PreviousButtonText,
  NextButtonDiv,
  NextButtonImage,
  NextButtonText,
  WarningDiv,
  WarningText,
} from './style'
import ModalContext from '../../../../contexts/ModalContext'
import StockContext from '../../../../contexts/StockContext'
import UserContext from '../../../../contexts/UserContext'
import cross from '../../../../assets/addStockModal/cross.svg'
import arrowRight from '../../../../assets/addStockModal/pages/arrow-right.svg'
import arrowLeft from '../../../../assets/addStockModal/pages/arrow-left.svg'
import {
  FieldsToHeaders,
  fieldsToHeaders,
  StockInterface,
} from '../../../../pages/ClientPanel/Stock/StockTable/utils'
import { Info } from 'lucide-react'
import 'font-awesome/css/font-awesome.min.css'

const SecondCustomAddPage: React.FC = () => {
  const { stock, setStock } = useContext(StockContext)
  const { setModalActive, setModalSection, itIsUpdate, stockMode, isVariation } =
    useContext(ModalContext)
  const { token, user } = useContext(UserContext)

  const [activeMenu, setActiveMenu] = useState(0)
  const [warningMessage, setWarningMessage] = useState('')
  const [isNextDisabled, setIsNextDisabled] = useState(true)
  const [errors, setErrors] = useState<{ [key: string]: boolean }>({})

  const isFashion = stockMode === 'fashion'

  const getHeaderTitle = () => {
    if (isVariation) return isFashion ? 'Criar Variação de Tecido' : 'Criar Variação de Estoque'
    if (isFashion) return itIsUpdate ? 'Editar Tecido' : 'Adicionar Tecido'
    return itIsUpdate ? 'Editar Estoque' : 'Adicionar Estoque'
  }
  const headerTitle = getHeaderTitle()

  useEffect(() => {
    setWarningMessage('')
  }, [stock])

  useEffect(() => {
    if (isFashion) {
      setIsNextDisabled(false)
      setErrors({})
      return
    }

    const requiredFields = ['dimension1']

    if (stock.measurementUnit === 'KG') {
      requiredFields.push('totalWeight', 'costPerKg')
    } else if (stock.measurementUnit === 'MM') {
      requiredFields.push('lengthInMm', 'costPerMm')
    }

    const allFieldsFilled = requiredFields.every(
      (field) => stock[field as keyof StockInterface],
    )

    setIsNextDisabled(!allFieldsFilled)

    const newErrors: { [key: string]: boolean } = {}
    requiredFields.forEach((field) => {
      newErrors[field] = !stock[field as keyof StockInterface]
    })
    setErrors(newErrors)
  }, [stock, isFashion])

  const applyPriceMask = (value: string): string => {
    const digits = value.replace(/\D/g, '')
    if (!digits) return ''
    const number = parseInt(digits, 10) / 100
    return number.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }

  const priceFields = ['costPerKg', 'costPerMm']

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    if (priceFields.includes(name)) {
      setStock({ ...stock, [name]: applyPriceMask(value) })
      return
    }
    if (isNaN(Number(value))) {
      setWarningMessage('Digite apenas números válidos!')
      return
    }
    setStock({ ...stock, [name]: value })
  }

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target
    setStock({ ...stock, [name]: value })
  }

  const previousPage = () => {
    setModalSection('firstCustomAdd')
  }

  const nextPage = () => {
    if (isFashion || !isNextDisabled) {
      setModalSection('thirdCustomAdd')
    }
  }

  const handleCalculate = (field: 'totalWeight' | 'lengthInMm') => {
    const { format, density, dimension1, dimension2, lengthInMm, totalWeight } =
      stock

    const fmt = (format || '').toUpperCase().trim()
    const dens = Number(density)
    const d1 = Number(dimension1)
    const d2 = Number(dimension2) || 0
    const length = Number(lengthInMm)
    const weight = Number(totalWeight)

    if (!fmt || !dens || !d1) {
      setWarningMessage('Preencha formato, densidade e dimensão 1!')
      return
    }

    let area = 0
    switch (fmt) {
      case 'BARRA':
      case 'REDONDO':
        area = Math.PI * (d1 / 2) ** 2
        break
      case 'QUADRADO':
      case 'CHAPA':
        if (!d2) {
          setWarningMessage('Informe a dimensão 2!')
          return
        }
        area = d1 * d2
        break
      case 'TUBO':
        if (!d2) {
          setWarningMessage('Para TUBO precisa das duas dimensões!')
          return
        }
        area = Math.PI * ((d1 / 2) ** 2 - (d2 / 2) ** 2)
        break
      case 'LINGOTE':
        if (!d2) {
          setWarningMessage('Informe a dimensão 2!')
          return
        }
        area = d1 * d2
        break
      default:
        setWarningMessage('Formato inválido!')
        return
    }

    if (field === 'totalWeight') {
      if (!length) {
        setWarningMessage('Informe o comprimento!')
        return
      }
      const newWeight = (area * length * dens) / 1_000_000
      setStock({ ...stock, totalWeight: String(newWeight.toFixed(2)) })
      setWarningMessage('')
    } else if (field === 'lengthInMm') {
      if (!weight) {
        setWarningMessage('Informe o peso!')
        return
      }
      const newLength = (weight * 1_000_000) / (area * dens)
      setStock({ ...stock, lengthInMm: String(newLength.toFixed(2)) })
      setWarningMessage('')
    }
  }

  const fieldsArray: string[][] = [
    ['quantity', 'totalWeight', 'totalCmc'],
    ['measurementUnit', 'dimension1', 'dimension2'],
    ['lengthInMm', 'costPerKg', 'costPerMm'],
  ]

  return (
    <Container>
      <HeaderDiv>
        <HeaderText>{headerTitle}</HeaderText>
        <CloseDiv onClick={() => setModalActive(false)}>
          <CloseImg src={cross} alt='Close tab' />
        </CloseDiv>
      </HeaderDiv>

      <ProgressBarDiv>
        <ProgressBarCircle className='active' />
        <ProgressBarLine className='active' />
        <ProgressBarCircle className='active' />
        <ProgressBarLine />
        <ProgressBarCircle />
      </ProgressBarDiv>

      {fieldsArray.map((inputRow, rowIndex) => (
        <InputRowDiv key={rowIndex}>
          {inputRow.map((stockField: string, colIndex) => (
            <InputDiv key={colIndex} style={{ position: 'relative' }}>
              <InputLabel htmlFor={stockField}>
                {stockField === 'lengthInMm'
                  ? 'Comprimento/mm'
                  : stockField === 'totalWeight'
                  ? 'Peso/Kg'
                  : fieldsToHeaders[stockField as keyof FieldsToHeaders]}
                {!isFashion && errors[stockField] !== undefined && (
                  <Info
                    size={15}
                    style={{ marginLeft: 5 }}
                    color={
                      !stock[stockField as keyof StockInterface]
                        ? 'red'
                        : 'gray'
                    }
                  />
                )}
              </InputLabel>

              {stockField !== 'measurementUnit' ? (
                <>
                  <Input
                    placeholder={priceFields.includes(stockField) ? '0,00' : 'Digite aqui'}
                    name={stockField}
                    value={stock[stockField as keyof StockInterface]}
                    onFocus={() => setActiveMenu(rowIndex * 3 + colIndex + 1)}
                    onBlur={() => setActiveMenu(0)}
                    onChange={handleChange}
                    autoComplete='off'
                  />
                  {!isFashion && (stockField === 'totalWeight' ||
                    stockField === 'lengthInMm') && (
                    <i
                      className='fa fa-calculator'
                      style={{
                        position: 'absolute',
                        right: 10,
                        top: 35,
                        cursor: 'pointer',
                        color: '#007bff',
                      }}
                      onClick={() =>
                        handleCalculate(
                          stockField as 'totalWeight' | 'lengthInMm',
                        )
                      }
                    />
                  )}
                </>
              ) : (
                <SelectInput
                  name={stockField}
                  value={stock[stockField as keyof StockInterface]}
                  onChange={handleSelectChange}
                >
                  <SelectOption value='KG'>KG</SelectOption>
                  <SelectOption value='MM'>MM</SelectOption>
                  {isFashion && <SelectOption value='UN'>UN</SelectOption>}
                </SelectInput>
              )}
            </InputDiv>
          ))}
        </InputRowDiv>
      ))}

      <ButtonsDiv>
        <WarningDiv>
          <WarningText>{warningMessage}</WarningText>
        </WarningDiv>

        <PreviousButtonDiv onClick={previousPage}>
          <PreviousButtonImage src={arrowLeft} alt='Right arrow icon' />
          <PreviousButtonText>Voltar</PreviousButtonText>
        </PreviousButtonDiv>

        <NextButtonDiv
          onClick={nextPage}
          style={{
            opacity: isFashion || !isNextDisabled ? 1 : 0.5,
            pointerEvents: isFashion || !isNextDisabled ? 'auto' : 'none',
          }}
        >
          <NextButtonImage src={arrowRight} alt='Right arrow icon' />
          <NextButtonText>Próximo</NextButtonText>
        </NextButtonDiv>
      </ButtonsDiv>
    </Container>
  )
}

export default SecondCustomAddPage
